import type { IFile } from '@sdk/contracts';
import { Button } from 'antd';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  AvatarWrapper,
  BoldText,
  Content,
  Description,
  DescriptionWrapper,
  Field,
  FooterStats,
  HeaderRow,
  Name,
  ShowMoreButton,
  StatCard,
  Subtitle,
  Title,
  Wrapper,
  Text,
  WrapperStats,
  ModalTitle,
  TagsWrapper,
  StyledModal
} from './ProfilePreviewModal.styles';

import { Icon } from '@/components/Icon';
import { Upload } from '@/components/Upload';
import { useIsMobile, useStudentProfilePreviewModalState } from '@/hooks';
import {
  useProfile,
  useStudentAverageQuizScore,
  useStudentGamificationRewards,
  useStudentStreak,
  useStudentSubscriptions
} from '@/store';
import { useUserInfoByUser } from '@/store/users/user-info';

export const StudentProfilePreviewModal: FC = () => {
  const {
    isOpen,
    selectedUser: profile,
    canEdit,
    handleClose,
    isLoading
  } = useStudentProfilePreviewModalState();
  const { t } = useTranslation('StudentProfileTranslations');
  const navigate = useNavigate();
  const isMobile = useIsMobile('xs');
  const [showFullDescription, setShowFullDescription] = useState(false);
  const { updateProfile } = useProfile();

  const userId = profile?._id ?? '';
  const { subscriptions } = useStudentSubscriptions(userId);
  const { averageScore } = useStudentAverageQuizScore(userId);
  const { rewards } = useStudentGamificationRewards(userId);
  const { streak } = useStudentStreak(userId);

  const progressAverage = useMemo(
    () =>
      subscriptions && subscriptions.length > 0
        ? subscriptions.reduce((acc, { progress }) => acc + progress, 0) / subscriptions.length
        : 0,
    [subscriptions]
  );
  const points = useMemo(() => rewards?.points ?? 0, [rewards]);
  const formattedAverageScore = useMemo(
    () => (typeof averageScore === 'number' ? averageScore.toFixed(2) : '-'),
    [averageScore]
  );

  const { userInfo } = useUserInfoByUser(userId);
  const isLong = !!(userInfo?.description && userInfo.description.length > 220);

  const handleEditProfile = () => {
    navigate('/profile/configuration?activeTab=SUMMARY');
  };

  const handleChangeAvatar = (file: IFile | null) => {
    if (file) updateProfile({ avatar: file });
  };

  const formatPoints = (value: number) => (value % 1 === 0 ? value.toFixed(0) : value.toFixed(1));

  if (!isOpen || isLoading || !profile) return null;

  return (
    <StyledModal
      open={isOpen}
      footer={null}
      closable
      width={isMobile ? '100%' : 600}
      onCancel={handleClose}
      title={<ModalTitle>{t('PROFILE')}</ModalTitle>}
      centered
      closeIcon>
      <Wrapper>
        <AvatarWrapper>
          <Upload
            preview
            shape="circle"
            margin="auto"
            accept="image/jpeg"
            width={100}
            value={profile.avatar as IFile}
            disabled={!canEdit}
            onChange={file => canEdit && handleChangeAvatar(file as IFile | null)}>
            {canEdit && <Icon i="Image" />}
          </Upload>
        </AvatarWrapper>
        <HeaderRow>
          <Name>{profile?.name}</Name>
          {canEdit && (
            <Button type="default" onClick={handleEditProfile} size="middle">
              {t('EDIT_PROFILE')}
            </Button>
          )}
        </HeaderRow>
        <DescriptionWrapper>
          <Title>{t('ABOUT_ME')}</Title>
          <Description $expanded={showFullDescription} $isEmpty={true}>
            {userInfo?.description}
          </Description>
          {isLong && !showFullDescription && (
            <ShowMoreButton onClick={() => setShowFullDescription(true)}>
              {t('SEE_MORE')}
            </ShowMoreButton>
          )}
        </DescriptionWrapper>

        <Content>
          <Field>
            <Title>{t('I_AM_STUDYING')}</Title>
            <TagsWrapper>
              {profile.tags.map(tag => (
                <Subtitle key={tag._id}>{tag.name}</Subtitle>
              ))}
            </TagsWrapper>
          </Field>

          <Title>{t('STATS')}</Title>
          <FooterStats>
            <StatCard>
              <span role="img" aria-label="lightning">
                ⚡️
              </span>
              <WrapperStats>
                <BoldText>{formatPoints(points)}</BoldText>
                <Text>{t('POINTS')}</Text>
              </WrapperStats>
            </StatCard>
            <StatCard>
              <span role="img" aria-label="fire">
                🔥
              </span>
              <WrapperStats>
                <BoldText>{t('STREAK_DAYS', { days: streak })}</BoldText>
                <Text>{t('LONGEST_STREAK')}</Text>
              </WrapperStats>
            </StatCard>
            <StatCard>
              <span role="img" aria-label="book">
                📚
              </span>
              <WrapperStats>
                <BoldText>{`${Math.round(progressAverage)}%`}</BoldText>
                <Text>{t('TOTAL_PROGRESS')}</Text>
              </WrapperStats>
            </StatCard>
            <StatCard>
              <span role="img" aria-label="pencil">
                📝
              </span>
              <WrapperStats>
                <BoldText>{formattedAverageScore}</BoldText>
                <Text>{t('AVERAGE_SCORE')}</Text>
              </WrapperStats>
            </StatCard>
          </FooterStats>
        </Content>
      </Wrapper>
    </StyledModal>
  );
};
